import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import { ChatProvider, useChat } from '../context/ChatContext';
import { useTheme } from '../context/ThemeContext';
import ChatSidebar from './chat/ChatSidebar';
import ChatWindow from './chat/ChatWindow';
import NotificationBadge from './chat/NotificationBadge';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MessagesPageContent = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { isDark } = useTheme();
  const { 
    conversations, 
    activeConversation, 
    setActiveConversation, 
    fetchConversations,
    unreadCount 
  } = useChat();
  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (token) {
      fetchConversations();
    }
  }, [token, fetchConversations]);

  const handleSelectConversation = (conversation) => {
    setActiveConversation(conversation);
    if (isMobile) {
      setShowSidebar(false);
    }
  };

  const handleStartNewChat = async (friend) => {
    // Check if conversation already exists
    const existingConv = conversations.find(c => 
      c.participants?.includes(friend.id)
    );

    if (existingConv) {
      handleSelectConversation(existingConv);
      return;
    }

    // Create a temporary conversation object for new chat
    const newConversation = {
      id: `new-${friend.id}`,
      participants: [user?.id, friend.id],
      participant_info: {
        id: friend.id,
        username: friend.username,
        display_name: friend.display_name,
        is_online: false
      },
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    setActiveConversation(newConversation);
    if (isMobile) {
      setShowSidebar(false);
    }
  };

  const handleBack = () => {
    setActiveConversation(null);
    setShowSidebar(true);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
            >
              <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-900'}`} />
            </button>
            <div className="flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-blue-500" />
              <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Messages</h1>
              <NotificationBadge count={unreadCount} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-60px)]">
        {/* Sidebar - Hidden on mobile when viewing a conversation */}
        {(showSidebar || !isMobile) && (
          <ChatSidebar 
            onSelectConversation={handleSelectConversation}
            onStartNewChat={handleStartNewChat}
          />
        )}

        {/* Chat Window - Always visible on desktop, toggles on mobile */}
        {(!showSidebar || !isMobile) && (
          <ChatWindow 
            conversation={activeConversation}
            onBack={handleBack}
          />
        )}
      </div>
    </div>
  );
};

// Wrapper with ChatProvider
const MessagesPage = () => {
  return (
    <ChatProvider>
      <MessagesPageContent />
    </ChatProvider>
  );
};

export default MessagesPage;
